import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { AccountData, DetailedPeriod, ExtendedDashboardOut, SummaryPeriod } from '../../../api';
import { TableComponent } from '../../../table/TableComponent';
import { CellProps } from '../../../table/useTable';
import { usePriceFormatter } from '../../../common/usePriceFormatter';
import { useAuth } from '../../../session/InternalAuthProvider';
import { getCurrencySymbol } from '../../../utils/utils';
import { SectionType, getVisibleSectionTypes } from '../sectionMeta';
import { SectionIcon } from '../SectionIcon';
import { ComparisonBadge } from '../ComparisonBadge';
import { calculateDailyAverage, calculatePercentageChange, getDaysInfo } from '../dashboardComparisonUtils';
import { mb } from '../../../common/theme/mbTokens';

type Props = {
  data?: ExtendedDashboardOut;
  isLoading: boolean;
};

type PeriodStats = { count: number; income: number; avgPerDay: number };
type Row = {
  sectionType: SectionType;
  thisMonth: PeriodStats;
  previousMonth: PeriodStats;
  sameMonthLastYear: PeriodStats;
};

const getMetric = (
  period: SummaryPeriod | DetailedPeriod | undefined,
  type: SectionType,
  metric: 'count' | 'income',
): number => ((period as any)?.[type]?.[metric] ?? 0) as number;

export const SectionBreakdownTable = ({ data, isLoading }: Props) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const currencySymbol = getCurrencySymbol(account);
  const priceFormatter = usePriceFormatter();
  const daysInfo = getDaysInfo();
  const showChange = daysInfo.dayOfMonth >= daysInfo.daysInCurrentMonth;

  const visibleSections = useMemo(() => getVisibleSectionTypes(account), [account]);

  const rows: Row[] = useMemo(
    () =>
      visibleSections.map((sectionType) => {
        const thisIncome = getMetric(data?.fromBeginningOfMonth, sectionType, 'income');
        const prevIncome = getMetric(data?.previousMonth, sectionType, 'income');
        const lyIncome = getMetric(data?.sameMonthLastYear, sectionType, 'income');
        return {
          sectionType,
          thisMonth: {
            count: getMetric(data?.fromBeginningOfMonth, sectionType, 'count'),
            income: thisIncome,
            avgPerDay: calculateDailyAverage(thisIncome, daysInfo.dayOfMonth),
          },
          previousMonth: {
            count: getMetric(data?.previousMonth, sectionType, 'count'),
            income: prevIncome,
            avgPerDay: calculateDailyAverage(prevIncome, daysInfo.daysInPreviousMonth),
          },
          sameMonthLastYear: {
            count: getMetric(data?.sameMonthLastYear, sectionType, 'count'),
            income: lyIncome,
            avgPerDay: calculateDailyAverage(lyIncome, daysInfo.daysInSameMonthLastYear),
          },
        };
      }),
    [data, visibleSections, daysInfo.dayOfMonth, daysInfo.daysInPreviousMonth, daysInfo.daysInSameMonthLastYear],
  );

  const renderPeriodCell = (stats: PeriodStats, muted?: boolean) => (
    <Grid container direction="column">
      <Grid container gap={0.5} alignItems="center">
        <Typography
          component="span"
          variant="body2"
          fontWeight={muted ? undefined : 700}
          color={muted ? 'text.secondary' : undefined}>
          {priceFormatter(stats.income, false, 'body2')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          · #{stats.count.toLocaleString()}
        </Typography>
      </Grid>
      <Grid container gap={0.5} alignItems="center">
        {priceFormatter(Math.round(stats.avgPerDay), false, 'caption')}
        <Typography variant="caption" color="text.secondary">
          {t('dashboard.avgPerDay')}
        </Typography>
      </Grid>
    </Grid>
  );

  const columns: CellProps<Row>[] = useMemo(
    () => [
      {
        id: 'section',
        alignment: 'left',
        label: t('analytics.summary.breakdown.section'),
        render: (row) => (
          <Grid container gap={0.5} alignItems="center" flexWrap="nowrap">
            <SectionIcon sectionType={row.sectionType} />
            <Typography variant="body2">{t(`analytics.sections.${row.sectionType}.title`)}</Typography>
          </Grid>
        ),
      },
      {
        id: 'thisMonth',
        alignment: 'left',
        label: t('analytics.summary.breakdown.thisMonth', { currency: currencySymbol }),
        render: (row) => renderPeriodCell(row.thisMonth),
      },
      {
        id: 'previousMonth',
        alignment: 'left',
        label: t('analytics.summary.breakdown.previousMonth', { currency: currencySymbol }),
        render: (row) => renderPeriodCell(row.previousMonth, true),
      },
      {
        id: 'sameMonthLastYear',
        alignment: 'left',
        label: t('analytics.summary.breakdown.sameMonthLastYear', { currency: currencySymbol }),
        render: (row) => renderPeriodCell(row.sameMonthLastYear, true),
      },
      {
        id: 'change',
        alignment: 'left',
        label: t('analytics.summary.breakdown.change'),
        render: (row) => {
          if (!showChange)
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          const change = calculatePercentageChange(
            row.thisMonth.income,
            row.previousMonth.income,
            row.sectionType === SectionType.REFUNDS,
          );
          return (
            <ComparisonBadge
              percentage={change.percentage}
              direction={change.direction}
              label={t('dashboard.vsPrevMonth')}
              isNew={row.previousMonth.income === 0 && row.thisMonth.income > 0}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, priceFormatter, showChange, currencySymbol],
  );

  return (
    <Grid container direction="column" gap={1.5}>
      <Typography variant="h6" sx={{ px: mb.layout.cardPadding }}>
        {t('analytics.summary.breakdown.title')}
      </Typography>
      <TableComponent<Row>
        isLoading={isLoading}
        data={rows}
        tableDetails={columns}
        hidePaging
        page={0}
        rowsPerPage={rows.length || 1}
        setPage={() => {}}
        setRowsPerPage={() => {}}
        scrollWithPage
      />
    </Grid>
  );
};
